<template lang="pug">
v-dialog(v-model="show" persistent max-width="600px" :fullscreen="$vuetify.breakpoint.smAndDown" )
    v-card.d-flex.flex-column(ref="mainCard" :disabled="!onCloseButtonClick")
        v-card-title.headline(ref="headline") {{ $t('moveBoarderToGroupDialog.dialogTitle') }}
        v-card-text.flex-shrink-0.flex-grow-1
            v-select(v-if="!isCustomerSelectorError"
                ref="customerSelector" v-model="selectedCustomer"
                append-icon="fas fa-chevron-down" outlined item-value="id" item-text="name" return-object
                :items="customerList" :label="$t('moveBoarderToGroupDialog.customerSelectorLabel')"
                :loading="isLoading" :disabled="isFormDisabled" @change="onCustomerChange"
            )
            v-select(v-if="!isCustomerSelectorError"
                ref="groupSelector" v-model="selectedGroup"
                append-icon="fas fa-chevron-down" outlined item-value="id" item-text="item.name" return-object
                :disabled="isFormDisabled" :items="filteredGroupList" :label="$t('moveBoarderToGroupDialog.groupSelectorLabel')"
                :loading="isLoading"
            )

                template(v-slot:selection="{item}")
                    div {{ item.name }}
                template(v-slot:item="{item}")
                    div {{ item.name }}

            v-alert(
                v-if="isCustomerSelectorError"
                dense
                border="left"
                type="warning"
                icon="far fa-exclamation-circle") {{ $t('moveBoarderToGroupDialog.errorMassageAllBoardersMustBeSameCustomer') }}

            v-card(flat)
                v-card-text.pt-0.text-center(ref="listLabelHeader") {{ $t('moveBoarderToGroupDialog.boarderListHeading') }}
                v-card-text.pa-0.boarder-list(:style="{maxHeight: listMaxHeight + 'px'}")
                    v-list(:two-line="$vuetify.breakpoint.mdAndUp" :three-line="$vuetify.breakpoint.smAndDown")
                        v-list-item(v-for="(boarder, idx) in currentBoarderList" :key="idx" )
                            v-list-item-action
                                v-checkbox(:disabled="isFormDisabled"
                                    v-model="boarder.isSelected" on-icon="fas fa-check-square" off-icon="fal fa-square"
                                    :readonly="!!operationResult" @change="onCheckChange"
                                )
                            v-list-item-content
                                v-list-item-title {{ formatBoarderName(boarder) }}
                                v-list-item-subtitle(v-if="isCustomerStrike(boarder)") {{ boarder.boarder.customer.name }}
                                v-list-item-subtitle(v-if="!isCustomerStrike(boarder)")
                                  strike {{ boarder.boarder.customer.name }}
                                    v-icon(style="font-size: small") fas fa-arrow-right
                                  span {{ selectedCustomer.name }}
                                template(v-if="!operationResult")
                                    v-list-item-subtitle {{ formatGroupLabel(boarder) }}
                                template(v-else)
                                    v-list-item-subtitle.d-inline-block(v-if="isCustomerStrike(boarder)") {{ formatGroupLabel(boarder) }}
                                    v-list-item-subtitle(v-if="!isCustomerStrike(boarder)")
                                      strike {{ formatGroupLabel(boarder) }}
                                        v-icon(style="font-size: small") fas fa-arrow-right
                                      span {{ selectedGroup.name }}


                            v-list-item-action(v-if="operationResult")
                                template(v-if="getIsWarningIcon(boarder)")
                                    v-tooltip(top)
                                        template(v-slot:activator="{ on, attrs }")
                                            v-btn(fab text v-if="'fad fa-times-circle'" v-bind="attrs" v-on="on")
                                                v-icon(:color="getResultIconColor(boarder)") {{ getResultIcon(boarder) }}
                                        span {{ $t('moveBoarderToGroupDialog.warningCountMessage') }}
                                template(v-else)
                                    v-btn(fab text v-if="'fad fa-times-circle'" )
                                        v-icon(:color="getResultIconColor(boarder)") {{ getResultIcon(boarder) }}

        v-divider
        v-card-actions(ref="bottomToolbar")
            v-spacer
            template(v-if="operationResult")
                v-btn(color="primary" @click="onCloseButtonClick" :disabled="isSaveInProgress") {{ $t('moveBoarderToGroupDialog.finishButtonLabel') }}
            template(v-else)
                v-btn(color="primary" @click="onSubmitButtonClick" :disabled="isSubmitButtonDisabled" :loading="isSaveInProgress") {{ submitButtonLabel }}
                v-btn(text :disabled="isSaveInProgress" @click="onCloseButtonClick") {{ $t('moveBoarderToGroupDialog.cancelButtonLabel') }}

</template>

<script lang="ts">
import Vue from 'vue'
import {AffectedBoarderItemResponse, BoarderGroupResponse, BoarderResponse} from '@/types/service-response'
import { Component, Prop, Watch } from 'vue-property-decorator'
import { MoveBoarderToGroupPayload } from '@/types/payload'
import _ from 'lodash'
import { BoarderSelectableListItem } from '@/types'
import { Action } from 'vuex-class'
import {
  BOARDER_FETCH_BOARDER_GROUP_LIST,
  BOARDER_MOVE_TO_GROUP,
  CUSTOMER_FETCH_CUSTOMER_LIST
} from '@/store/action-types'
import BoarderGroup from '@/model/boarder/BoarderGroup'
import Customer from "@/model/customer/Customer";

@Component({})
export default class MoveBoarderToGroupDialog extends Vue {
  @Prop({ type: Boolean, default: false }) readonly show!: boolean
  @Prop({ type: Array, required: true }) readonly boarderList!: BoarderResponse[]

  $refs!: {
    mainCard: Vue,
    headline: HTMLElement,
    customerSelector: Vue,
    groupSelector: Vue,
    listLabelHeader: HTMLElement,
    bottomToolbar: HTMLElement
  }

  private currentBoarderList: BoarderSelectableListItem[] = []
  private isSaveInProgress: boolean = false
  private operationResult: BoarderGroupResponse | null = null
  private groupList: BoarderGroup[] = []
  private selectedGroup: BoarderGroup | null = null
  private listMaxHeight: number = 0
  private isLoading: boolean = false
  private customerList: Customer[] = []
  private selectedCustomer: Customer | null = null

  @Action(BOARDER_MOVE_TO_GROUP) performMove!: { (payload: MoveBoarderToGroupPayload): Promise<BoarderGroupResponse> }
  @Action(BOARDER_FETCH_BOARDER_GROUP_LIST) fetchBoarderGroupList!: { (): Promise<BoarderGroup[]> }
  @Action(CUSTOMER_FETCH_CUSTOMER_LIST) customFetchList!: { (): Promise<Customer[]> }

  get submitButtonLabel (): string {
    const count = this.currentBoarderList.filter((b: BoarderSelectableListItem) => b.isSelected).length

    return this.$tc('moveBoarderToGroupDialog.submitButtonLabel', count, { count })
  }

  get isFormDisabled (): boolean {
    return this.isSaveInProgress || !!this.operationResult || false
  }

  get isSubmitButtonDisabled (): boolean {
    const selectedCount = this.currentBoarderList.filter((b) => b.isSelected).map((b) => b.boarder.id).length

    return selectedCount === 0 || !this.selectedGroup || this.isCustomerSelectorError
  }

  get isCustomerSelectorError (): boolean {
    const selectedBoarderList = this.currentBoarderList.filter((b) => b.isSelected)
    return !selectedBoarderList.every((b) => b.boarder.customer.id === selectedBoarderList[0].boarder.customer.id)
  }



  get filteredGroupList (): BoarderGroup[] {
    if(this.selectedCustomer) {
      return this.groupList.filter((g) => this.selectedCustomer!.id === g.customer?.id)
    } else {
      return []
    }
  }

  @Watch('filteredGroupList')
  onFilteredGroupListChange (newVal: BoarderGroup[]): void {
    if (newVal.length === 0) {
      this.selectedGroup = null
    }
  }

  onCustomerChange (): void {
    if (this.selectedCustomer && this.filteredGroupList.length > 0) {
      this.selectedGroup = this.filteredGroupList[0]
    }
  }

  onCheckChange (): void {
    const selectedBoarderList = this.currentBoarderList.filter((b) => b.isSelected)
    if(this.customerList && selectedBoarderList.length > 0) {
      const foundCustomer: Customer | undefined = this.customerList.find((item: Customer) => item.id === selectedBoarderList[0].boarder.customer.id)
      this.selectedCustomer = foundCustomer ? foundCustomer : null
      const foundGroup: BoarderGroup | undefined = this.filteredGroupList.find((item: BoarderGroup) => item.id === selectedBoarderList[0].boarder.boarderGroupCollection[0].id)
      this.selectedGroup = foundGroup ? foundGroup : null
    } else {
      this.selectedCustomer = null
      this.selectedGroup = null
    }
  }

  async fetchData (): Promise<void> {
    this.isLoading = true

    this.groupList = await this.fetchBoarderGroupList()
    const selectedBoarderList = this.currentBoarderList.filter((b) => b.isSelected)
    const foundGroup: BoarderGroup | undefined = this.filteredGroupList.find((item: BoarderGroup) => item.id === selectedBoarderList[0].boarder.boarderGroupCollection[0].id)
    this.selectedGroup = foundGroup ? foundGroup : null

    this.isLoading = false
  }

  formatBoarderName (boarder: BoarderSelectableListItem): string {
    return `${boarder.boarder.person.firstName} ${boarder.boarder.person.familyName}`
  }

  formatGroupLabel (boarder: BoarderSelectableListItem): string {
    return boarder.boarder.boarderGroupCollection.map((g) => g.name).join(', ')
  }

  getResultIcon (boarder: BoarderSelectableListItem): string {
    if (this.wasOperationSuccessful(boarder)) {
      return 'far fa-check-circle'
    }

    return 'fad fa-times-circle'
  }

  isCustomerStrike (boarder: BoarderSelectableListItem): boolean {
    return !this.wasOperationSuccessful(boarder);


  }

  getIsWarningIcon(boarder: BoarderSelectableListItem): boolean {
    return !this.wasOperationSuccessful(boarder);

  }

  getResultIconColor (boarder: BoarderSelectableListItem): string {
    if (this.wasOperationSuccessful(boarder)) {
      return 'green'
    }

    return 'warning'
  }

  wasOperationSuccessful (boarder: BoarderSelectableListItem): boolean {
    const resultBoarder: AffectedBoarderItemResponse | undefined = this.operationResult?.affectedBoarders.find((b: AffectedBoarderItemResponse) => b.id === boarder.boarder.id)
    const hasBoarderGroup = resultBoarder?.boarderGroupCollection.some(g => g.id === this.selectedGroup?.id)

    return !!resultBoarder && hasBoarderGroup || false
  }

  calculateListMaxHeight (): number {
    let height = 400

    if (this.$vuetify.breakpoint.smAndDown) {
      if (this.$refs.mainCard) {
        const mainHeight = window.screen.height
        const headlineHeight = this.$refs.headline.clientHeight
        const customerSelectorHeight = (this.$refs.customerSelector.$vnode.elm as HTMLElement).clientHeight
        const groupSelectorHeight = (this.$refs.groupSelector.$vnode.elm as HTMLElement).clientHeight
        const labelHeaderHeight = this.$refs.listLabelHeader.clientHeight
        const bottomToolbarHeight = this.$refs.bottomToolbar.clientHeight

        height = mainHeight - headlineHeight - customerSelectorHeight - groupSelectorHeight - labelHeaderHeight - bottomToolbarHeight - 21
      }
    }

    return height
  }

  async onSubmitButtonClick (): Promise<void> {
    const payload: MoveBoarderToGroupPayload = {
      boarderIds: this.currentBoarderList.filter((b) => b.isSelected).map((b) => b.boarder.id.toString()),
      targetBoarderGroupId: this.selectedGroup!.id
    }
    try {
      this.isSaveInProgress = true
      this.operationResult = await this.performMove(payload)
    } catch (e) {

    } finally {
      this.isSaveInProgress = false
    }
  }

  async customerFetchList (): Promise<Customer[]> {
    this.customerList = await this.$store.dispatch(CUSTOMER_FETCH_CUSTOMER_LIST)
    const selectedBoarderList = this.currentBoarderList.filter((b) => b.isSelected)
    if(this.customerList && selectedBoarderList.length > 0) {
      const foundCustomer: Customer | undefined = this.customerList.find((item: Customer) => item.id === selectedBoarderList[0].boarder.customer.id)
      this.selectedCustomer = foundCustomer ? foundCustomer : null
    }
    return this.customerList
  }

  onCloseButtonClick (): void {
    this.$emit('close', this.operationResult !== null)
  }

  onOrientationChange (): void {
    setTimeout(() => this.listMaxHeight = this.calculateListMaxHeight(), 3000)
  }

  created () {
    screen.orientation.addEventListener('change', this.onOrientationChange)
  }

  mounted (): void {
    this.customerFetchList()
    this.currentBoarderList = _.cloneDeep(this.boarderList)
      .sort((a, b) => a.person.familyName.localeCompare(b.person.familyName))
      .map((b: BoarderResponse): BoarderSelectableListItem => {
        return {
          boarder: b,
          isSelected: true
        }
      })

    this.fetchData()

    this.$nextTick(() => this.listMaxHeight = this.calculateListMaxHeight())
  }

  /*beforeDestroy () {
    screen.orientation.removeEventListener('change', this.onOrientationChange)
  }*/
}

</script>

<style lang="stylus" scoped>
.boarder-list
    overflow auto

.group-item-customer
    font-size small

</style>
